/**
 * NotificationContext (R4.2)
 *
 * Provides a global unread notification count that:
 * - Is fetched on mount (when user is authenticated).
 * - Polls every 30 seconds to keep the AppBar badge in sync.
 * - Can be manually refreshed by pages (e.g., NotificationsPage after mark-read).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { notificationsApi } from '../api/notifications';
import { useAuth } from './AuthContext';

const POLL_INTERVAL_MS = 30_000;

interface NotificationContextValue {
  unreadCount: number;
  /** Call after marking notifications as read to immediately update the badge. */
  refresh: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const count = await notificationsApi.unreadCount();
      setUnreadCount(count);
    } catch {
      // Silently fail — badge stays at last known value
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void fetchCount();
      }
    };
    tick();
    intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user, fetchCount]);

  const refresh = useCallback(() => {
    fetchCount();
  }, [fetchCount]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refresh }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be inside NotificationProvider');
  return ctx;
}

/** Test-only provider: fixed unread count, no polling / API. */
export function NotificationTestProvider({
  children,
  unreadCount = 0,
}: {
  children: React.ReactNode;
  unreadCount?: number;
}) {
  return (
    <NotificationContext.Provider value={{ unreadCount, refresh: () => undefined }}>
      {children}
    </NotificationContext.Provider>
  );
}
